import React, { useEffect } from 'react'
import {
  setCrossClientResults,
  setCrossClientResultsExecution,
  setCrossClientResultsExecutionLoading,
  setCrossClientTeammates,
  stopCrossClientExecutionPolling,
  resetCrossClientResults,
  selectCrossClientFetchedClientIds,
  addCrossClientClients,
  setCrossClientClientsLoading,
  setCrossClientTeammatesLoading,
  addCrossClientFetchedClientIds,
  selectCrossClientDisplayedResults,
  selectCrossClientResultsLoading,
  setCrossClientResultsLoading,
} from '../../../store/crossClient/crossClientResults'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery, useLazyGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'

export interface CrossClientResultsInitializationProps {
  executionId: string
}

export const CrossClientResultsInitialization: React.FC<CrossClientResultsInitializationProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const displayedResults = useAppSelector(selectCrossClientDisplayedResults)
  const fetchedClientIds = useAppSelector(selectCrossClientFetchedClientIds)
  const resultsLoading = useAppSelector(selectCrossClientResultsLoading)

  const [triggerGetCrossClientQueryExecution] = useLazyGetCrossClientQueryExecutionQuery()
  const [triggerGetAllCrossClientQueryResults] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  useEffect(() => {
    const getCrossClientResults = async () => {
      try {
        const results = await triggerGetAllCrossClientQueryResults({ accountId, executionId }).unwrap()
        if (!results.data?.length) {
          return
        }
        dispatch(setCrossClientResults(results.data))
      } catch (e) {
        console.error(e)
        dispatch(stopCrossClientExecutionPolling())
      } finally {
        dispatch(setCrossClientResultsLoading(false))
      }
    }
    const getExecution = async () => {
      try {
        dispatch(setCrossClientResultsExecutionLoading(true))
        const execution = await triggerGetCrossClientQueryExecution({ accountId, executionId }).unwrap()
        dispatch(setCrossClientResultsExecution(execution))
        await getCrossClientResults()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientResultsExecutionLoading(false))
      }
    }
    getExecution()
  }, [dispatch, accountId, executionId, triggerGetCrossClientQueryExecution, triggerGetAllCrossClientQueryResults])

  useEffect(() => {
    const getCrossClientTeammates = async () => {
      try {
        const teammates = await triggerGetAllUsers({ accountId, identityTypes: ['idp'] }).unwrap()
        if (teammates?.data) {
          dispatch(setCrossClientTeammates(teammates.data))
        }
      } finally {
        dispatch(setCrossClientTeammatesLoading(false))
      }
    }
    getCrossClientTeammates()
  }, [dispatch, accountId, triggerGetAllUsers])

  useEffect(() => {
    if (resultsLoading || !displayedResults.length) {
      return
    }

    const missingClientIds = displayedResults.map((result) => result.clientId).filter((clientId) => !fetchedClientIds[clientId])
    if (!missingClientIds.length) {
      return
    }

    const fetchMissingClients = async () => {
      dispatch(setCrossClientClientsLoading(true))
      dispatch(addCrossClientFetchedClientIds(missingClientIds))
      try {
        const contacts = await triggerGetAllUsers({ accountId, ids: missingClientIds }).unwrap()
        if (contacts?.data) {
          const clients = contacts.data.filter((contact) => contact.roles.some((role) => role.name === 'Client'))
          dispatch(addCrossClientClients(clients))
        }
      } finally {
        dispatch(setCrossClientClientsLoading(false))
      }
    }

    fetchMissingClients()
  }, [dispatch, accountId, resultsLoading, displayedResults, fetchedClientIds, triggerGetAllUsers])

  useEffect(() => {
    return () => {
      dispatch(resetCrossClientResults())
    }
  }, [dispatch])

  return null
}
